import { useToast } from "native-base";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getStore } from "store/storeUtils";
import { CREATE_MODALS } from "global/constants";
import { apiSingleEmployeeData, apiSingleUpdate, fetchEmployeeRequestById } from "api/apiCall";
import { useMutation } from "@tanstack/react-query";
import AmplitudeHelper from "utils/analytics";
import { useTranslation } from "react-i18next";
import { getBanksList, getCategoriesList, getGroupsAndPayDatesList } from "api/useQueryCalls";
import { getDifferenceBetweenObjects } from "utils/helpers";
import { validationCheck } from "utils/validation";
import { fieldNames } from "global/fieldNameMapping";
import { checkForActiveHandler, showErrorToast } from "utils/utilities";
import useEmployeeDetails from "utils/employee/useEmployeeDetails";

const useUpdateConnect = ({ isEmployeeRequest = false }) => {
  const { t } = useTranslation("common");
  const toast = useToast();
  const navigate = useNavigate();
  const { modal, setModal, resetLoginData } = getStore();

  const [formData, setFormData] = useState({
    birthDate: new Date(),
    suspensionCategory: "",
    suspendedReason: "",
  });
  const [errors, setErrors] = useState({});
  const { groupPayDates, handleGroupPayDates, handleBanksListError, showCreateUpdateSuccessToast } =
    useEmployeeDetails();

  const handleCancel = () => {
    AmplitudeHelper.logEvent("cancel_button_clicked", {
      source: "employee_single_creation_page",
    });
    setModal({ ...modal, type: CREATE_MODALS.singleCreateCancel });
  };

  const mutateEmployeeData = (data = {}) => {
    const { firstName = "", lastName = "", payDate = "-1" } = data;
    return {
      ...data,
      name: `${firstName ?? ""}`.concat(` ${lastName ?? ""}`),
      phone: data?.mobileNumber,
      ktp: data?.ktpNumber,
      employeeId: data?.employeeCode,
      bankName: { bankName: data?.bankName },
      dateJoined: new Date(data?.joinedAt),
      group: data?.groupName ?? "",
      bankNumber: data?.bankAccountNo,
      salary: data?.salaryAmount,
      employeeType: { data: data?.collarType },
      gender: t(data?.gender),
      status: data?.employeeStatus,
      suspensionCategory: data?.suspensionCategory ?? "",
      suspendedReason: data?.suspendedReason ?? "",
      payDate,
      hasPendingUpdateRequest: isEmployeeRequest ? true : data?.hasPendingUpdateRequest,
      cutOffDate: payDate == "-1" ? t("home.list.rows.endMonth") : payDate ?? "-",
    };
  };

  const { data: selectedEmployeeData, mutate: getEmployeeId } = useMutation({
    mutationFn: (employeeId) => apiSingleEmployeeData(employeeId),
  });

  const { data: employeeRequestData, mutate: getEmployeeRequestById } = useMutation({
    mutationFn: (employeeId) => fetchEmployeeRequestById(employeeId),
  });

  const { data: categoriesData = [] } = getCategoriesList();

  const {
    isSuccess: updateSuccess,
    isError: isUpdateError,
    error: updateError,
    mutate: updateEmployee,
    data: singleEmployeeUpdateResponse,
  } = useMutation({ mutationFn: (data) => apiSingleUpdate(data) });

  const { isError: banksListIsError, error: banksListError, data: banksListData = [] } = getBanksList();

  const { isSuccess: getGroupsPayDatesIsSuccess, data: groupsPayDatesData = [] } = getGroupsAndPayDatesList();

  useEffect(() => {
    handleGroupPayDates({
      getGroupsPayDatesIsSuccess,
      groupsPayDatesData,
    });
  }, [getGroupsPayDatesIsSuccess]);

  useEffect(() => {
    handleBanksListError({
      banksListIsError,
      banksListError,
      toast,
      navigate,
      resetLoginData,
    });
  }, [banksListIsError, banksListError]);

  useEffect(() => {
    if (selectedEmployeeData != {} && selectedEmployeeData) {
      setFormData(mutateEmployeeData(selectedEmployeeData));
    } else if (employeeRequestData != {} && employeeRequestData) {
      setFormData(mutateEmployeeData(employeeRequestData));
    }
  }, [selectedEmployeeData, employeeRequestData]);

  useEffect(() => {
    if (isUpdateError) {
      const newErrors = {};

      updateError.forEach((errorCode) => {
        const field = fieldNames[errorCode]; // map field name to particular error code and select out that one field which corresponds to the error code
        if (field) {
          newErrors[`${field}`] = t(`errorCodes.${errorCode}`);
        }
      });

      if (Object.keys(newErrors).length != 0) {
        setErrors(newErrors);
      } else {
        showErrorToast({ error: updateError, toast, navigate, resetLoginData });
      }
    }
  }, [isUpdateError, updateError]);

  useEffect(() => {
    if (updateSuccess) {
      showCreateUpdateSuccessToast({
        singleEmployeeUpdateResponse,
        navigate,
        toast,
        isCreate: false,
      });
    }
  }, [updateSuccess]);

  const handleSubmit = (values) => {
    const newErrors = {};
    const required = [
      "name",
      "phone",
      // "birthDate", removed from Mandatory fields discussed with Desy and team
      "ktp",
      "employeeId",
      // "group", removed from Mandatory fields discussed with Desy and team
      "bankNumber",
      "bankName",
      "beneficiaryName",
      "employeeType",
      "status",
      "salary",
      "payDate",
      "gender",
      "dateJoined",
    ];
    const inputs = [
      // "name",
      "phone",
      // "ktp",
      "birthDate",
      "dateJoined",
      // 'employeeId',
      // "bankNumber",
      // "beneficiaryName",
      // "salary",
    ];

    required.forEach((field) => {
      const fieldValue = values[field];
      if (field === "employeeType") {
        if (!fieldValue.data || fieldValue.data.toString().trim() == "") {
          newErrors[`${field}`] = "basicValidation.missingFields";
        }
      } else if (!fieldValue || fieldValue.toString().trim() == "") {
        newErrors[`${field}`] = "basicValidation.missingFields";
      }
    });

    inputs.forEach((field) => {
      let validation = "";

      if (field === "birthDate") {
        validation = validationCheck[`${field}`](values[`${new Date(field)}`]);
      } else {
        validation = validationCheck[`${field}`](values[`${field}`]);
      }
      if (validation != "" && newErrors[`${field}`] != "basicValidation.missingFields") {
        newErrors[`${field}`] = validation;
      }
    });

    let initialData = {};
    if (selectedEmployeeData != {} && selectedEmployeeData) {
      initialData = selectedEmployeeData;
    } else if (employeeRequestData != {} && employeeRequestData) {
      initialData = employeeRequestData;
    }

    if (Object.keys(newErrors).length != 0) {
      setErrors(newErrors);
    } else {
      setErrors({});
      setFormData(values);
      AmplitudeHelper.logEvent("save_button_clicked", {
        source: "detailed_employee_page",
      });
      setModal({
        ...modal,
        type: CREATE_MODALS.singleUpdate,
        data: {
          ...values,
          shouldShowDescription: values?.status !== "active" && initialData?.employeeStatus !== values?.status,
        },
      });
    }
  };

  const mutateData = (data) => {
    const nameParts = data?.name.trim().split(" ");
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(" ");
    const basicDetails = {
      requestType: "update_employee",
      employeeId: data?.id,
      userId: data?.userId,
    };
    const mutatedData = {
      firstName,
      lastName,
      birthDate: data?.birthDate ? dayjs(data.birthDate).format("YYYY-MM-DD") : undefined,
      mobileNumber: data?.phone,
      gender: data?.gender,
      ktpNumber: data?.ktp,
      employeeCode: data?.employeeId,
      joinedAt: dayjs(data?.dateJoined).format("YYYY-MM-DD"),
      collarType: data?.employeeType?.data,
      groupName: data?.group ? (data?.group === t("employeeCreation.single.noGroup") ? "" : data?.group) : "",
      salaryType: "FIXED_MONTH",
      payDate: data?.payDate,
      salaryAmount: data?.salary,
      bankName: data?.bankName?.bankName,
      bankId: data?.bankName?.id,
      bankAccountNo: data?.bankNumber,
      beneficiaryName: data?.beneficiaryName,
      employeeStatus: formData?.status === "Aktif" || formData?.status === "active" ? "active" : formData?.status,
    };

    let initialData = {};
    if (selectedEmployeeData != {} && selectedEmployeeData) {
      initialData = selectedEmployeeData;
    } else if (employeeRequestData != {} && employeeRequestData) {
      initialData = employeeRequestData;
    }
    const employeeStatusData =
      initialData?.suspensionCategory !== formData?.suspensionCategory ||
      initialData?.suspendedReason !== formData?.suspendedReason
        ? {
            suspensionCategory: formData.suspensionCategory !== "" ? formData.suspensionCategory : null,
            suspendedReason:
              formData.suspendedReason !== "" && formData.suspensionCategory.includes("Other")
                ? formData.suspendedReason
                : null,
          }
        : {};

    const groupDetails =
      (initialData?.group || "") !== data?.group
        ? {
            groupName: data?.group === t("employeeCreation.single.noGroup") ? "" : data?.group,
          }
        : {};

    const differences = getDifferenceBetweenObjects({
      object1: initialData,
      object2: mutatedData,
    });

    const updatedData = {
      ...differences,
      ...basicDetails,
      ...employeeStatusData,
      ...groupDetails,
    };

    const resultantData = checkForActiveHandler(formData, updatedData);

    return resultantData;
  };

  const handleSendData = () => {
    const sendData = mutateData(formData);
    AmplitudeHelper.logEvent("save_button_clicked", {
      source: "detailed_employee_page",
      employee_id: sendData?.employeeId,
    });
    updateEmployee(sendData);
  };

  return {
    formData,
    banksList: banksListData?.list || [],
    getEmployeeId,
    getEmployeeRequestById,
    setFormData,
    handleSendData,
    handleSubmit,
    handleCancel,
    errors,
    setErrors,
    categoriesData,
    groupPayDates,
  };
};

export default useUpdateConnect;
